/**
 * LoginPage — Sign-in for survey authors
 *
 * Uses the Hanko Flow API v1.5.x handler-based pattern.
 *
 * The SDK's flow.init() drives the state machine:
 *   flow.init(path, handlers) → handler(state) → returns action.run() → SDK calls next handler
 *
 * Login flow:  preflight → login_init → login_method_chooser → login_password → [onboarding] → success
 * Signup flow: preflight → registration_init → password_creation → [onboarding] → success
 *
 * User inputs are stored in refs so handlers can access current values.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import hanko from '../hanko-init';
import SamLogo from './SamLogo';
import PasskeySetup from './PasskeySetup';

export default function LoginPage({ onLoginSuccess }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [flowState, setFlowState] = useState(null);
    const [totpCode, setTotpCode] = useState('');
    const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);

    // Refs so flow handlers can access current user input values
    const emailRef = useRef('');
    const passwordRef = useRef('');
    const totpRef = useRef('');
    const initialized = useRef(false);

    const handlersRef = useRef(null);

    // Keep refs in sync with state
    useEffect(() => { emailRef.current = email; }, [email]);
    useEffect(() => { passwordRef.current = password; }, [password]);
    useEffect(() => { totpRef.current = totpCode; }, [totpCode]);

    const resetForm = () => { setError(''); setMessage(''); };

    // ── Wait for Hanko session cookie to be set ────────────
    const waitForSession = useCallback(async () => {
        for (let i = 0; i < 15; i++) {
            if (hanko.session.isValid()) {
                console.log('[LoginPage] session valid after', i * 200, 'ms');
                if (onLoginSuccess) onLoginSuccess();
                return;
            }
            await new Promise(r => setTimeout(r, 200));
        }
        // Fallback: try anyway
        console.log('[LoginPage] session wait timed out, calling onLoginSuccess anyway');
        if (onLoginSuccess) onLoginSuccess();
    }, [onLoginSuccess]);

    // ── Show state in UI for user input ─────────────────────
    const showState = useCallback((state) => {
        if (state?.error?.message) setError(state.error.message);
        setFlowState(state);
        setLoading(false);
    }, []);

    // ── Route manual .run() results to the correct handler ──
    const processFlowResult = useCallback(async (result) => {
        if (!result || !result.name) { setLoading(false); return; }
        console.log('[LoginPage] processFlowResult:', result.name);
        const handlers = handlersRef.current;
        if (handlers && handlers[result.name]) {
            const next = await handlers[result.name](result);
            if (next && next.name) await processFlowResult(next);
        } else {
            showState(result);
        }
    }, [showState]);

    // ── WebAuthn preflight (shared) ─────────────────────────
    const handlePreflight = async (state) => {
        const webauthnAvailable = !!(window.PublicKeyCredential);
        let platform = false, conditional = false;
        try { platform = webauthnAvailable && await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { }
        try { conditional = webauthnAvailable && window.PublicKeyCredential.isConditionalMediationAvailable && await window.PublicKeyCredential.isConditionalMediationAvailable(); } catch { }
        // Return action — SDK will process returned state and call next handler
        return state.actions.register_client_capabilities({
            webauthn_available: webauthnAvailable,
            webauthn_platform_authenticator_available: platform,
            webauthn_conditional_mediation_available: conditional,
        }).run();
    };

    // ── Initialize ──────────────────────────────────────────
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;
        startLoginFlow();
    }, []);

    // ── Start login flow ────────────────────────────────────
    const startLoginFlow = useCallback(() => {
        setLoading(false);
        setError('');
        setFlowState(null);
        const handlers = {
            onError: (err) => {
                console.error('[LoginPage] Flow error:', err);
                setError(err.message || 'An unexpected error occurred');
                setLoading(false);
            },
            preflight: handlePreflight,
            // User input needed — show form
            login_init: (state) => {
                console.log('[LoginPage] login_init — showing email form');
                showState(state);
                // Don't return — wait for user to submit form
            },
            // Auto-advance to password login
            login_method_chooser: (state) => {
                console.log('[LoginPage] login_method_chooser — auto-advancing to password');
                if (state.actions?.continue_to_password_login) {
                    return state.actions.continue_to_password_login().run();
                }
                showState(state); // fallback: show chooser
            },
            // User input needed — show password form (or auto-submit if password already entered)
            login_password: (state) => {
                console.log('[LoginPage] login_password — password available:', !!passwordRef.current);
                if (passwordRef.current) {
                    // Password already entered — submit automatically
                    return state.actions.password_login({ password: passwordRef.current }).run();
                }
                showState(state); // show password field
            },
            login_passkey: (state) => showState(state),
            login_otp: (state) => {
                console.log('[LoginPage] login_otp — totp available:', !!totpRef.current, 'actions:', Object.keys(state.actions || {}));
                if (totpRef.current) {
                    const action = state.actions?.otp_code_verify || state.actions?.otp_code_validate;
                    if (action) return action({ otp_code: totpRef.current }).run();
                }
                showState(state);
            },
            login_password_recovery: (state) => showState(state),
            login_security_key: (state) => showState(state),
            passcode_confirmation: (state) => {
                if (totpRef.current) {
                    return state.actions.verify_passcode({ code: totpRef.current }).run();
                }
                showState(state);
            },
            // Skip onboarding steps during login — user already set these up during registration
            credential_onboarding_chooser: (state) => {
                if (state.actions?.skip) return state.actions.skip().run();
                showState(state);
            },
            onboarding_create_passkey: (state) => {
                if (state.actions?.skip) return state.actions.skip().run();
                showState(state);
            },
            onboarding_email: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            onboarding_username: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            device_trust: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            success: async (state) => {
                console.log('[LoginPage] success!');
                // Wait for session to become valid
                for (let i = 0; i < 15; i++) {
                    if (hanko.session.isValid()) break;
                    await new Promise(r => setTimeout(r, 200));
                }
                // Check if user needs passkey registration
                try {
                    const resp = await fetch('/api/console/me', { credentials: 'include' });
                    if (resp.ok) {
                        const profile = await resp.json();
                        const prefs = profile?.preferences || {};
                        if (prefs.passkey_required && !prefs.passkey_registered) {
                            console.log('[LoginPage] passkey required — showing setup prompt');
                            setShowPasskeyPrompt(true);
                            setLoading(false);
                            return; // Don't navigate yet
                        }
                    }
                } catch (err) {
                    console.warn('[LoginPage] Could not check passkey preference:', err);
                }
                // No passkey needed, proceed normally
                if (onLoginSuccess) onLoginSuccess();
                setLoading(false);
            },
            error: (state) => showState(state),
        };
        handlersRef.current = handlers;
        hanko.flow.init('/login', handlers);
    }, [showState]);

    // ── Start registration flow ─────────────────────────────
    const startRegistrationFlow = useCallback(() => {
        setLoading(false);
        setError('');
        setFlowState(null);
        hanko.flow.init('/registration', {
            onError: (err) => {
                console.error('[LoginPage] Registration error:', err);
                setError(err.message || 'Registration failed');
                setLoading(false);
            },
            preflight: handlePreflight,
            registration_init: (state) => {
                console.log('[LoginPage] registration_init — showing signup form');
                showState(state);
            },
            password_creation: (state) => {
                console.log('[LoginPage] password_creation — password available:', !!passwordRef.current);
                if (passwordRef.current) {
                    return state.actions.register_password({ new_password: passwordRef.current }).run();
                }
                showState(state);
            },
            passcode_confirmation: (state) => {
                if (totpRef.current) {
                    return state.actions.verify_passcode({ code: totpRef.current }).run();
                }
                showState(state);
            },
            credential_onboarding_chooser: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            onboarding_create_passkey: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            onboarding_email: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            onboarding_username: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            device_trust: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            success: (state) => {
                console.log('[LoginPage] registration success!');
                waitForSession();
            },
            error: (state) => showState(state),
        });
    }, [showState]);

    // ── Passkey Sign-In ─────────────────────────────────────
    const handlePasskeySignIn = useCallback(async () => {
        resetForm();
        setLoading(true);
        try {
            const actions = flowState?.actions;
            if (actions?.webauthn_generate_request_options) {
                // This will restart the flow.init chain — but we need to handle it
                // For passkeys, just call the action directly
                await actions.webauthn_generate_request_options().run();
            } else {
                setError('Passkey sign-in is not available.');
                setLoading(false);
            }
        } catch (err) {
            console.error('[LoginPage] Passkey error:', err);
            setError(err.name === 'NotAllowedError'
                ? 'Passkey authentication was cancelled.'
                : 'Passkey sign-in failed. Try email + password.');
            setLoading(false);
        }
    }, [flowState]);

    // ── Email + Password Sign-In ────────────────────────────
    // Instead of calling action.run() here, we restart the flow with current inputs.
    // The flow.init handlers will auto-submit at each step because refs have the values.
    const handleSignIn = useCallback(async (e) => {
        e.preventDefault();
        resetForm();

        const stateName = flowState?.name;

        // If at login_password (2-step flow), submit password directly
        if (stateName === 'login_password' && flowState.actions?.password_login && password) {
            setLoading(true);
            try {
                const result = await flowState.actions.password_login({ password }).run();
                console.log('[LoginPage] password result:', result?.name, result?.status);
                await processFlowResult(result);
            } catch (err) {
                setError(err.message || 'Sign in failed');
                setLoading(false);
            }
            return;
        }

        // If at TOTP/passcode, submit code
        if ((stateName === 'login_otp' || stateName === 'passcode_confirmation') && totpCode) {
            setLoading(true);
            try {
                let result;
                if (stateName === 'login_otp') {
                    const action = flowState.actions?.otp_code_verify || flowState.actions?.otp_code_validate;
                    if (action) result = await action({ otp_code: totpCode }).run();
                } else if (flowState.actions?.verify_passcode) {
                    result = await flowState.actions.verify_passcode({ code: totpCode }).run();
                }
                console.log('[LoginPage] TOTP result:', result?.name, result?.status);
                await processFlowResult(result);
            } catch (err) {
                setError(err.message || 'Verification failed');
                setLoading(false);
            }
            return;
        }

        // At login_init — restart the flow. The handlers will auto-submit
        // email at login_init, auto-advance through method_chooser,
        // and auto-submit password at login_password (since refs have values).
        setLoading(true);
        const handlers = {
            onError: (err) => {
                console.error('[LoginPage] Flow error:', err);
                setError(err.message || 'An unexpected error occurred');
                setLoading(false);
            },
            preflight: handlePreflight,
            login_init: (state) => {
                console.log('[LoginPage] login_init — auto-submitting email:', emailRef.current);
                const action = state.actions?.continue_with_login_identifier;
                if (action && emailRef.current) {
                    const inputData = action.inputs?.email ? { email: emailRef.current }
                        : action.inputs?.identifier ? { identifier: emailRef.current }
                            : { email: emailRef.current };
                    return action(inputData).run();
                }
                showState(state);
            },
            login_method_chooser: (state) => {
                console.log('[LoginPage] login_method_chooser — auto-advancing');
                if (state.actions?.continue_to_password_login) {
                    return state.actions.continue_to_password_login().run();
                }
                showState(state);
            },
            login_password: (state) => {
                console.log('[LoginPage] login_password — auto-submitting password');
                if (passwordRef.current && state.actions?.password_login) {
                    return state.actions.password_login({ password: passwordRef.current }).run();
                }
                showState(state);
            },
            login_passkey: (state) => showState(state),
            login_otp: (state) => {
                console.log('[LoginPage] login_otp — showing TOTP input, actions:', Object.keys(state.actions || {}));
                showState(state); // Show TOTP input — user hasn't entered code yet in this flow
            },
            login_password_recovery: (state) => showState(state),
            login_security_key: (state) => showState(state),
            passcode_confirmation: (state) => showState(state),
            credential_onboarding_chooser: (state) => {
                if (state.actions?.skip) return state.actions.skip().run();
                showState(state);
            },
            onboarding_create_passkey: (state) => {
                if (state.actions?.skip) return state.actions.skip().run();
                showState(state);
            },
            onboarding_email: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            onboarding_username: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            device_trust: (state) => state.actions?.skip ? state.actions.skip().run() : showState(state),
            success: async (state) => {
                console.log('[LoginPage] login success!');
                // Wait for session to become valid
                for (let i = 0; i < 15; i++) {
                    if (hanko.session.isValid()) break;
                    await new Promise(r => setTimeout(r, 200));
                }
                // Check if user needs passkey registration
                try {
                    const resp = await fetch('/api/console/me', { credentials: 'include' });
                    if (resp.ok) {
                        const profile = await resp.json();
                        const prefs = profile?.preferences || {};
                        if (prefs.passkey_required && !prefs.passkey_registered) {
                            console.log('[LoginPage] passkey required — showing setup prompt');
                            setShowPasskeyPrompt(true);
                            setLoading(false);
                            return;
                        }
                    }
                } catch (err) {
                    console.warn('[LoginPage] Could not check passkey preference:', err);
                }
                // No passkey needed, proceed normally
                if (onLoginSuccess) onLoginSuccess();
                setLoading(false);
            },
            error: (state) => showState(state),
        };
        handlersRef.current = handlers;
        hanko.flow.init('/login', handlers);
    }, [flowState, email, password, totpCode, showState, processFlowResult]);





    // ── Rendering ───────────────────────────────────────────
    const stateName = flowState?.name;
    const showTOTPField = stateName === 'login_otp' || stateName === 'passcode_confirmation' || stateName === 'mfa_otp_verification';
    const showPasswordOnly = stateName === 'login_password';
    const showPasswordCreation = stateName === 'password_creation';

    // TOTP / Passcode screen
    if (showTOTPField) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.logoArea}>
                        <SamLogo size={64} showWordmark variant="onWhite" />
                        <p style={styles.subtitle}>
                            {stateName === 'passcode_confirmation' ? 'Enter Verification Code' : 'Two-Factor Authentication'}
                        </p>
                    </div>
                    <form onSubmit={handleSignIn} style={styles.form}>
                        <div style={styles.field}>
                            <label htmlFor="totpCode" style={styles.label}>
                                {stateName === 'passcode_confirmation' ? 'Verification Code' : 'Authentication Code'}
                            </label>
                            <input id="totpCode" type="text" placeholder="Enter code"
                                value={totpCode} onChange={(e) => setTotpCode(e.target.value)}
                                disabled={loading} required autoComplete="one-time-code" autoFocus
                                style={styles.input} />
                        </div>
                        {error && <p style={styles.error}>{error}</p>}
                        <button type="submit" disabled={loading}
                            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}>
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                        <button type="button" onClick={() => { setTotpCode(''); startLoginFlow(); }}
                            style={styles.toggleButton}>Cancel</button>
                    </form>
                </div>
            </div>
        );
    }

    // Passkey setup prompt (shown after login when passkey_required is set)
    if (showPasskeyPrompt) {
        return (
            <div style={styles.container}>
                <div style={{ ...styles.card, maxWidth: 440 }}>
                    <div style={styles.logoArea}>
                        <SamLogo size={64} showWordmark variant="onWhite" />
                    </div>
                    <h2 style={{ fontSize: 18, fontWeight: 600, color: '#1E293B', margin: '0 0 8px', textAlign: 'center' }}>
                        Set Up Passkey Login
                    </h2>
                    <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 20px', textAlign: 'center', lineHeight: 1.5 }}>
                        Your administrator requires passkey authentication. Register a passkey to secure your account.
                    </p>
                    <PasskeySetup
                        onComplete={() => {
                            setShowPasskeyPrompt(false);
                            if (onLoginSuccess) onLoginSuccess();
                        }}
                    />
                    <button
                        onClick={() => {
                            setShowPasskeyPrompt(false);
                            if (onLoginSuccess) onLoginSuccess();
                        }}
                        style={{
                            display: 'block', width: '100%', marginTop: 12,
                            padding: '8px 16px', border: 'none', background: 'transparent',
                            color: '#94A3B8', fontSize: 12, cursor: 'pointer', textAlign: 'center',
                        }}
                    >
                        Skip for now
                    </button>
                </div>
            </div>
        );
    }

    // Main Login / Sign-Up screen
    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.logoArea}>
                    <SamLogo size={64} showWordmark variant="onWhite" />
                </div>

                {!showPasswordOnly && (
                    <>
                        <button onClick={handlePasskeySignIn} disabled={loading}
                            style={{ ...styles.passkeyButton, ...(loading ? styles.buttonDisabled : {}) }}>
                            <span style={styles.passkeyIcon}>🔑</span>
                            {loading ? 'Please wait...' : 'Sign in with Passkey'}
                        </button>
                        <div style={styles.divider}>
                            <span style={styles.dividerLine} />
                            <span style={styles.dividerText}>or use email</span>
                            <span style={styles.dividerLine} />
                        </div>
                    </>
                )}

                <h2 style={styles.formTitle}>
                    {showPasswordOnly ? 'Enter Password' : 'Sign In with Email'}
                </h2>

                <form onSubmit={handleSignIn} style={styles.form}>
                    {/* Email — hide at login_password and password_creation */}
                    {!showPasswordOnly && !showPasswordCreation && (
                        <div style={styles.field}>
                            <label htmlFor="email" style={styles.label}>Email</label>
                            <input id="email" type="email" placeholder="you@example.com"
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                disabled={loading} required autoComplete="email webauthn"
                                style={styles.input} />
                        </div>
                    )}

                    {/* Password — always show for login/signup */}
                    {(stateName === 'login_init' || showPasswordOnly || showPasswordCreation || !stateName) && (
                        <div style={styles.field}>
                            <label htmlFor="password" style={styles.label}>Password</label>
                            <input id="password" type="password" placeholder="Enter your password"
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                disabled={loading} required minLength={8}
                                autoComplete={showPasswordCreation ? 'new-password' : 'current-password'}
                                style={styles.input} />
                        </div>
                    )}

                    {showPasswordCreation && (
                        <div style={styles.field}>
                            <label htmlFor="confirmPassword" style={styles.label}>Confirm Password</label>
                            <input id="confirmPassword" type="password" placeholder="Confirm your password"
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                disabled={loading} required minLength={8} autoComplete="new-password"
                                style={styles.input} />
                        </div>
                    )}

                    {error && <p style={styles.error}>{error}</p>}
                    {message && <p style={styles.message}>{message}</p>}

                    <button type="submit" disabled={loading}
                        style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}>
                        {loading ? 'Please wait...' :
                            (showPasswordOnly ? 'Sign In' : 'Continue')}
                    </button>
                </form>


            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
        backgroundColor: 'var(--color-bg-base)',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    },
    card: {
        backgroundColor: '#FFFFFF', borderRadius: '8px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        padding: '40px', width: '100%', maxWidth: '400px',
    },
    logoArea: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' },
    subtitle: { fontSize: '14px', color: '#5F6368', margin: '8px 0 0 0' },
    passkeyButton: {
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        width: '100%', padding: '12px 16px', fontSize: '15px', fontWeight: '600',
        color: '#FFFFFF', backgroundColor: '#1A73E8', border: 'none', borderRadius: '6px',
        cursor: 'pointer', transition: 'background-color 0.15s', marginBottom: '0', fontFamily: 'inherit',
    },
    passkeyIcon: { fontSize: '18px' },
    divider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' },
    dividerLine: { flex: 1, height: '1px', backgroundColor: '#DADCE0' },
    dividerText: { fontSize: '13px', color: '#80868B', whiteSpace: 'nowrap' },
    formTitle: { fontSize: '16px', fontWeight: '600', color: '#3C4043', marginBottom: '16px' },
    form: { display: 'flex', flexDirection: 'column', gap: '16px' },
    field: { display: 'flex', flexDirection: 'column', gap: '4px' },
    label: { fontSize: '14px', fontWeight: '500', color: '#3C4043' },
    input: {
        padding: '8px 12px', fontSize: '14px', border: '1px solid #DADCE0', borderRadius: '4px',
        outline: 'none', transition: 'border-color 0.15s', color: '#202124',
    },
    error: {
        color: '#F44336', fontSize: '13px', margin: 0, padding: '8px 12px',
        backgroundColor: '#FFEBEE', borderRadius: '4px',
    },
    message: {
        color: '#4CAF50', fontSize: '13px', margin: 0, padding: '8px 12px',
        backgroundColor: '#E8F5E9', borderRadius: '4px',
    },
    button: {
        padding: '8px 16px', fontSize: '14px', fontWeight: '500', color: '#FFFFFF',
        backgroundColor: '#5B9FFF', border: 'none', borderRadius: '4px', cursor: 'pointer',
        transition: 'background-color 0.15s', marginTop: '4px',
    },
    buttonDisabled: { backgroundColor: '#BDC1C6', cursor: 'not-allowed' },
    toggleArea: { textAlign: 'center', marginTop: '20px' },
    toggleButton: {
        background: 'none', border: 'none', color: '#5B9FFF', cursor: 'pointer',
        fontSize: '14px', textDecoration: 'underline', fontFamily: 'inherit',
    },
};
